/**
 * @file paddle Wave Summit 2020 直播日历
 * @author FengGuang(fengguang01@baidu.com)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import classNames from 'classnames';
import { Button, Popover } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { TooltipAlignConfig } from 'antd/es/tooltip';

import { ICalendarTableDay, ILiveCalendar, ILiveCalendarLiveListItem, ILiveCalendarLiveTypeItem, IControlParams } from './types';
import A from '../../../../components/a/A';


const alignConfig: TooltipAlignConfig = {
    points: ['tl', 'bl'],
    offset: [40, -15]
};


const LiveCalendar: React.FC<ILiveCalendar> = props => {

    const [date, setDate] = useState<Moment>(() => {
        return moment();
    });
    const [now, setNow] = useState<Moment>(() => {
        return moment();
    });

    // props.liveList作为初始值，initLiveList是为了筛选
    const [initLiveList, setInitLiveList] = useState<ILiveCalendarLiveListItem[]>(() => {
        return props.liveList;
    });

    const [controlParams, setControlParams] = useState<IControlParams>({liveTypesKey: '', position: ''})

    useEffect(() => {
        setInitLiveList((initLiveList) => {
            return initLiveList.filter((item: ILiveCalendarLiveListItem) => {
                return !controlParams.liveTypesKey || item.liveTypesKey === controlParams.liveTypesKey
            })
        })
    }, [controlParams]);

    useEffect(() => {
        const h = setInterval(() => {
            setNow(moment());
        }, 10000);
        return () => {
            clearInterval(h);
        };
    }, [setNow]);

    // 直播列表
    const liveList = useMemo<ILiveCalendarLiveListItem[]>(() => {
        const getLiveState = (now: Moment, startTime?: Moment, endTime?: Moment): 'before' | 'current' | 'after' => {
            if (startTime && now.isBefore(startTime)) {
                return 'before';
            }
            else if (endTime && now.isAfter(endTime)) {
                return 'after';
            }
            return 'current';
        };

        const list = (initLiveList || []).map((live) => {
            return {
                ...live,
                liveState: getLiveState(now, live.startTime, live.endTime)
            };
        });
        list.sort((a, b) => {
            if (a.startTime && b.startTime) {
                return a.startTime.unix() - b.startTime.unix();
            }
            return 0;
        });
        return list;
    }, [initLiveList, now]);

    // 直播列表的map
    const liveListMapByDay = useMemo(() => {
        const theListMapByDay = new Map<string, ILiveCalendarLiveListItem[]>();
        liveList.forEach((item) => {
            const key = item.startTime?.format('YYYY-MM-DD') ?? '';
            const list = theListMapByDay.get(key) || [];
            list.push(item);
            theListMapByDay.set(key, list);
        });

        return theListMapByDay;
    }, [liveList]);


    // 直播类型的map
    const liveTypes = props.liveTypes;
    const liveTypesMap = useMemo(() => {
        return new Map<string, ILiveCalendarLiveTypeItem>(
            (liveTypes ?? []).map((type) => ([type.liveTypesKey, type]))
        );
    }, [liveTypes]);

    // 星期几的数组
    const weekDayText = useMemo(() => {
        const now = moment();
        const firstDay = now.clone().startOf('week');
        const lastDay = firstDay.clone().endOf('week');
        const lastDayUnix = lastDay.unix();
        const current = firstDay.clone();
        const weekText: string[] = [];
        while (current.unix() < lastDayUnix) {
            weekText.push(current.format('dddd'));
            current.add(1, 'day');
        }
        return weekText;
    }, []);


    // 日历本体的数组
    const _dayArr = useMemo<ICalendarTableDay[][]>(() => {
        const firstDay = date.clone().startOf('month').startOf('week');
        const lastDay = date.clone().endOf('month').endOf('week');
        const lastDayUnix = lastDay.unix();
        const dayList: Moment[] = [];
        const current = firstDay.clone();
        const limit = 60;
        for (let l = limit; current.unix() < lastDayUnix && l; l--) {
            dayList.push(current.clone());
            current.add(1, 'day');
        }
        // 按照 week 分组
        const dayWeekList: Moment[][] = [];
        for (let i = 0, l = limit; dayList.length > 0 && l; l--, i++) {
            dayWeekList.push(dayList.splice(0, weekDayText.length));
        }
        return dayWeekList.map(week => (week.map(day => ({
            day,
            liveList: []
        }))));
    }, [date, weekDayText.length]);

    // 日历本体+直播列表
    const _dayArr2 = useMemo<ICalendarTableDay[][]>(() => {
        // 直播列表添加到日历中
        return _dayArr.map(week => (week.map((dayObj) => {
            return {
                ...dayObj,
                liveList: liveListMapByDay.get(dayObj.day.format('YYYY-MM-DD')) || []
            };
        })));
    }, [_dayArr, liveListMapByDay]);


    // 日历本体的数组增加时间状态
    const dayArr = useMemo<ICalendarTableDay[][]>(() => {
        return _dayArr2.map(week => (week.map(dayObj => ({
            ...dayObj,
            diff: (() => {
                if (dayObj.day.isAfter(now, 'day')) {
                    return 'next';
                }
                else if (dayObj.day.isBefore(now, 'day')) {
                    return 'prev';
                }
                return 'today';
            })()
        }))));
    }, [now, _dayArr2]);

    const handleChangeToPrevMonth = useCallback(() => {
        setDate((oldDate) => {
            return oldDate.clone().add(-1, 'month');
        });
    }, []);

    const handleChangeToNextMonth = useCallback(() => {
        setDate((oldDate) => {
            return oldDate.clone().add(1, 'month');
        });
    }, [setDate]);

    const validLiveList = useMemo(() => {
        return liveList.filter(item => !!item.startTime);
    }, [liveList]);

    const prevMonthBtnDisabled = useMemo(() => {
        const firstDay = date.clone().startOf('month');
        const firstLive = validLiveList[0];
        return !firstLive?.startTime || !firstDay || firstLive.startTime?.isAfter(firstDay);
    }, [validLiveList, date]);
    const nextMonthBtnDisabled = useMemo(() => {
        const lastDay = date.clone().endOf('month');
        const lastLive = validLiveList[validLiveList.length - 1];
        return !lastLive?.startTime || !lastDay || !lastLive.startTime?.isAfter(lastDay);
    }, [validLiveList, date]);

    return (
        <div>
            <div className="paddle-live-live-calendar-top-line clear-both">

                <div className="paddle-live-live-calendar-type-wrap">
                    {liveTypes.map(type => (
                        <div
                            key={`${type.liveTypesKey}${type.title}`}
                            className="paddle-live-live-calendar-type"
                        >
                            <span style={{ borderColor: type.color }}>{type.title}</span>
                        </div>
                    ))}
                </div>
                <div>
                    <div className="paddle-live-live-calendar-date">
                        <Button
                            className="paddle-live-live-calendar-date-btn"
                            type="link"
                            icon={<LeftOutlined />}
                            disabled={prevMonthBtnDisabled}
                            onClick={handleChangeToPrevMonth}
                        />
                        {date.format('YYYY年MM月')}
                        <Button
                            className="paddle-live-live-calendar-date-btn"
                            type="link"
                            icon={<RightOutlined />}
                            disabled={nextMonthBtnDisabled}
                            onClick={handleChangeToNextMonth}
                        />
                    </div>
                    {/*<div className="paddle-live-live-calendar-title">*/}
                    {/*    <span className="paddle-live-live-calendar-title-icon">“ </span>*/}
                    {/*    {props.title}*/}
                    {/*    <span className="paddle-live-live-calendar-title-icon"> ”</span>*/}
                    {/*</div>*/}
                </div>
            </div>
            <div className="paddle-live-live-calendar-table-wrap">
                <table className="paddle-live-live-calendar-table">
                    <thead>
                        <tr>
                            {weekDayText.map(text => (
                                <th key={text}>{text}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {dayArr.map((week, index) => (
                            <tr key={week[0]?.day.unix() ?? index}>
                                {week.map(day => (
                                    <td
                                        key={day.day.unix()}
                                        className={`paddle-live-live-calendar-table-${day.diff}`}
                                    >
                                        <div className="paddle-live-live-calendar-table-daynum">
                                            <span>{day.day.format('D')}</span>
                                        </div>

                                        {day.liveList.map(live => (
                                            <Popover
                                                key={live.key}
                                                placement="bottomLeft"
                                                overlayClassName="paddle-live-live-calendar-table-live-item-popover"
                                                align={alignConfig as any}
                                                content={
                                                    <React.Fragment>
                                                        <div
                                                            className="paddle-live-live-calendar-table-live-item-popover-title"
                                                        >
                                                            {live.title}
                                                        </div>
                                                        <div
                                                            className="paddle-live-live-calendar-table-live-item-popover-time"
                                                        >
                                                            {live.startTime?.format('YY.MM.DD HH:mm') ?? ''}
                                                        -
                                                        {!!(live.startTime && live.endTime && live.endTime.isSame(live.startTime, 'day'))
                                                                ? live.endTime.format('HH:mm')
                                                                : (live.endTime?.format('YY.MM.DD HH:mm') ?? '')
                                                            }
                                                        </div>
                                                        <div
                                                            className="paddle-live-live-calendar-table-live-item-popover-info-wrap"
                                                        >
                                                            {!!live.avatar
                                                                && (
                                                                    <div
                                                                        className="paddle-live-live-calendar-table-live-item-popover-info-left"
                                                                    >
                                                                        <span
                                                                            className="paddle-live-live-calendar-table-live-item-popover-avatar"
                                                                            style={{ backgroundImage: live.avatar ? `url(${live.avatar})` : undefined }}
                                                                        />
                                                                    </div>
                                                                )}
                                                            <div
                                                                className="paddle-live-live-calendar-table-live-item-popover-info"
                                                            >
                                                                <div>
                                                                    主讲人：{live.author}
                                                                </div>
                                                                <div>
                                                                    {live.authorTitle}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="paddle-live-live-calendar-table-live-item-popover-btns"
                                                        >
                                                            {!!(live.liveState === 'before' && live.beforeBtnText)
                                                                && (
                                                                    <A
                                                                        href={live.beforeLink}
                                                                        target
                                                                    >
                                                                        <Button
                                                                            className="paddle-live-live-calendar-table-live-item-popover-btn"
                                                                            type="primary"
                                                                        >
                                                                            {live.beforeBtnText}
                                                                        </Button>
                                                                    </A>
                                                                )}
                                                            {!!(live.liveState === 'current' && live.btnText)
                                                                && (
                                                                    <A
                                                                        href={live.link}
                                                                        target
                                                                    >
                                                                        <Button
                                                                            className="paddle-live-live-calendar-table-live-item-popover-btn"
                                                                            type="primary"
                                                                        >
                                                                            {live.btnText}
                                                                        </Button>
                                                                    </A>
                                                                )}
                                                            {(live.liveState === 'after' && (!live.afterBtnText || !live.afterLink))
                                                                && (
                                                                    <Button
                                                                        className="paddle-live-live-calendar-table-live-item-popover-btn"
                                                                        type="primary"
                                                                        disabled
                                                                    >
                                                                        回放链接生成中
                                                                    </Button>
                                                                )}
                                                            {!!(live.liveState === 'after' && live.afterBtnText)
                                                                && (
                                                                    <A
                                                                        href={live.afterLink}
                                                                        target
                                                                    >
                                                                        <Button
                                                                            className="paddle-live-live-calendar-table-live-item-popover-btn"
                                                                            type="primary"
                                                                        >
                                                                            {live.afterBtnText}
                                                                        </Button>
                                                                    </A>
                                                                )}
                                                        </div>
                                                    </React.Fragment>
                                                }
                                            >
                                                <div
                                                    className={classNames(
                                                        'paddle-live-live-calendar-table-live-item',
                                                        live.liveState === 'current' && 'paddle-live-live-calendar-table-live-item-current'
                                                    )}
                                                    style={{ borderLeftColor: liveTypesMap.get(live.liveTypesKey ?? '')?.color }}
                                                >
                                                    <div>
                                                        {live.title}
                                                    </div>
                                                    <div className="paddle-live-live-calendar-table-live-item-time">
                                                        {live.liveState === 'current'
                                                            ? '正在直播'
                                                            : live.startTime?.format('HH:mm') ?? ''
                                                        }
                                                    </div>
                                                </div>
                                            </Popover>
                                        ))}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LiveCalendar;
